/**
 * useKeyboardNavigation - Excel-style keyboard navigation for trip lists
 *
 * Provides keyboard shortcuts:
 * - Arrow Up/Down: Navigate between trips
 * - Space: Cycle through states on focused trip
 * - L: Cycle state on focused trip
 * - Shift+L: Lock all trips
 * - Shift+U: Unlock all trips
 * - Shift+E: Exclude all trips
 * - Cmd/Ctrl+A: Include all trips
 * - Escape: Clear focus
 */

import React, { useCallback, useEffect, useRef, useState } from "react";

export interface KeyboardNavigationCallbacks {
  onCycleTripState: (tripId: string) => void;
  onLockAll: () => void;
  onUnlockAll: () => void;
  onExcludeAll: () => void;
  onIncludeAll: () => void;
}

export interface KeyboardNavigationResult {
  /** Currently focused item index (-1 if none) */
  focusedIndex: number;
  /** Set the focused index */
  setFocusedIndex: React.Dispatch<React.SetStateAction<number>>;
  /** Ref for the list element (for scrolling) */
  listRef: React.RefObject<HTMLUListElement | null>;
  /** Ref for the container element (for focusing) */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Keyboard event handler to attach to container */
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

/**
 * Hook for Excel-style keyboard navigation in trip lists
 *
 * @param tripIds - Array of trip IDs in display order
 * @param callbacks - Object containing action callbacks
 * @param filterText - Current filter text (resets focus when changed)
 */
export function useKeyboardNavigation(
  tripIds: string[],
  callbacks: KeyboardNavigationCallbacks,
  filterText: string
): KeyboardNavigationResult {
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const listRef = useRef<HTMLUListElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { onCycleTripState, onLockAll, onUnlockAll, onExcludeAll, onIncludeAll } = callbacks;

  // Keyboard handler for Excel-style navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInInput = target.tagName === "INPUT" || target.tagName === "TEXTAREA";

      // Arrow keys can work from input to start navigation (blur input and focus container)
      if (isInInput && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        target.blur();
        containerRef.current?.focus();
        // Fall through to handle the arrow key
      } else if (isInInput) {
        // Don't intercept other keyboard events from input fields
        return;
      }

      if (tripIds.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev < tripIds.length - 1 ? prev + 1 : prev;
            // If starting from -1 (no focus), start at 0
            return prev === -1 ? 0 : next;
          });
          break;
        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case " ": // Space - cycle through states
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < tripIds.length) {
            onCycleTripState(tripIds[focusedIndex]);
          }
          break;
        case "l":
        case "L":
          if (e.shiftKey) {
            // Shift+L: Lock all
            onLockAll();
          } else if (focusedIndex >= 0 && focusedIndex < tripIds.length) {
            // L: Cycle state on focused trip
            onCycleTripState(tripIds[focusedIndex]);
          }
          break;
        case "u":
        case "U":
          if (e.shiftKey) {
            // Shift+U: Unlock all
            onUnlockAll();
          }
          break;
        case "e":
        case "E":
          if (e.shiftKey) {
            // Shift+E: Exclude all
            onExcludeAll();
          }
          break;
        case "a":
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            // Cmd/Ctrl+A: Include all
            onIncludeAll();
          }
          break;
        case "Escape":
          setFocusedIndex(-1);
          break;
      }
    },
    [focusedIndex, tripIds, onCycleTripState, onLockAll, onUnlockAll, onExcludeAll, onIncludeAll]
  );

  // Reset focus when filter changes
  useEffect(() => {
    setFocusedIndex(-1);
  }, [filterText]);

  // Auto-scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll("[data-trip-index]");
      items[focusedIndex]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [focusedIndex]);

  return {
    focusedIndex,
    setFocusedIndex,
    listRef,
    containerRef,
    handleKeyDown,
  };
}
